/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import { camelCase, flow, isArray, upperFirst } from 'lodash';
import { uuidToDbId } from '@/graphql/utils/filters';
import { ZenFilterAbbreviated } from '@/components/listing/components/filters/types';
import { ColumnSortDirection, SortBy } from '@/components/listing/types';

const upperCamelCase = flow(camelCase, upperFirst);

// Returns object type from mapped attribute filterName (in backendKey format)
const mapSyntaxToObjectType = (filterName: string): string => filterName.split(':')[1];

/**
 * TODO: TEMP implementation until GQL filter param accepts new filters syntax
 *
 * This implementation builds the old filters syntax from the current new SoR
 * Listing filters structure. It converts for example:
 * [{
 *   "f": "title",
 *   "o": "contains",
 *   "v": "foo"
 *  }, {
 *   "f": "description",
 *   "o": "doesNotContain",
 *   "v": "bar"
 * }]
 * to: "title ~ \"foo\" AND description ~ \"bar\""
 */
export function composeOldFilterSyntax(filters: Filter[], attributeMetaByName): string {
  const composeValue: (value: any) => string = value => (isNaN(value) ? `"${value}"` : `${value}`);

  // TODO: clean this up
  const f = filters
    .map((filter: ZenFilterAbbreviated) => {
      let filterName: string = filter.f;
      // Old filter syntax needs `backendKey` format
      if (attributeMetaByName && filter.f in attributeMetaByName) {
        filterName = attributeMetaByName[filter.f].backendKey;
      }
      let operator: string = filter.o;
      // TODO can't value be a bool as well? or a number? This should be fixed all over the place
      const values: string | undefined = isArray(filter.v)
        ? '[' + filter.v.map(composeValue).join(', ') + ']'
        : composeValue(filter.v);

      if (operator === 'IS_EMPTY') {
        operator = 'IS';
      }
      if (operator === 'IS_NOT_EMPTY') {
        operator = 'IS';
      }

      // TODO: get from gql isMapping field (pass in via attributeMetaByName param)
      if (filterName.includes('__mapping__:')) {
        // TODO: TEMP
        //   we need to add object type title field from gql for mappings (or just
        //   the object type of which it's a mapping so we can get metadata for it)
        const toRelevantExpObjectType = flow(mapSyntaxToObjectType, upperCamelCase);

        filterName = toRelevantExpObjectType(filterName);

        // TODO: TEMP will be changed when # syntax is replaced in query parser

        if (operator == '=') {
          // TODO: TEMP, although this is the most appropriate way to do it,
          //   since the filter contains the UUID, and we need the `dbId` only
          //   because the GQL query's `filter` param requires it like that
          const dbId = uuidToDbId(filter.v);

          return `#${filterName},${dbId}#`;
        }
        if (operator == 'MAPPED') return `#${filterName},ANY#`;
        if (operator == 'NO_MAPPED') return `#${filterName},NONE#`;
      }

      // TODO: handle potential errs

      return `"${filterName}" ${operator} ` + values;
    })
    .join(' AND ');

  return f;
}

/**
 * This implementation builds an old SoR listing filter structure from the new SoR Listing
 * filter structure. It's closely related to `composeOldFilterSyntax`, but the output
 * is a bit different.
 *
 * From:
 * ```
 * [{
 *   "f": "title",
 *   "o": "contains",
 *   "v": "foo"
 *  }, {
 *   "f": "description",
 *   "o": "doesNotContain",
 *   "v": "bar"
 * }]
 * ```
 *
 * To:
 * ```
 * {
 *   title: {
 *     op: "~",
 *     value: "foo"
 *   },
 *   description: {
 *     op: "~",
 *     value: "bar"
 *   }
 * }
 * ```
 * @param filters An array of Filter objects
 * @param attributeMetaByName A map from `name` to `backendKey` as returned by
 *                            objectType.attrs GraphQL query
 *
 * @return an object with structure { field: { op: "operator", value: "value" } }
 */
export function composeOldListingFilterSyntax(filters: ZenFilterAbbreviated[], attributeMetaByName): object {
  const f = filters
    .map(filter => {
      const filterName: string = attributeMetaByName[filter.f].backendKey;
      const operator: string = filter.o;
      const values = filter.v;

      const out: any = {};
      out[filterName] = { op: operator, value: values };
      return out;
    })
    .reduce((final, fil) => ({ ...final, ...fil }), {});

  return f;
}

/**
 * Creates a filter for filtering by ids
 *
 * @param  ids  Object ids [1,2]
 *
 * Example :
 * {
 *   id: {
 *     op: "in_array",
 *     value: [1,2]
 *   },
 * }
 *
 **/
export function composeOldFilterByIdsQuery(ids: string[]): object {
  return {
    id: {
      op: 'in_array',
      value: ids,
    },
  };
}

/**
 * Creates a sort by from new sort
 *
 * @param  sort  {
 *      by: field
 *      direction: ColumnSortDirection
 * }
 *
 * Example to :
 *
 *   ascending:title
 *
 **/
export function composeOldSortSyntax(sort: SortBy): string {
  if (sort === undefined) {
    return '';
  }
  let direction = sort.direction == ColumnSortDirection.Asc ? 'ascending' : 'descending';
  return direction + ':' + sort.by;
}
